import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, UserCheck, Eye, ChevronDown } from "lucide-react";
import { IMAGES } from "../data/content";
import { MaskedLine, EASE, scrollToSection } from "../lib/motion";
import { t } from "../i18n";

const TRUST = [
  { icon: UserCheck, label: "Professional Guidance" },
  { icon: ShieldCheck, label: "Personalized Assistance" },
  { icon: Eye, label: "Transparent Process" },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden grain">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="International travel — airplane wing over a glowing horizon"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/90 via-navy/80 to-navy-deep" />
      <div className="absolute inset-0 bg-gradient-to-r from-navy-deep/90 via-navy-deep/45 to-navy-deep/60" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24 w-full">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
          className="text-xs sm:text-sm font-semibold tracking-[0.35em] text-gold font-display mb-6"
          data-testid="hero-eyebrow"
        >
          {t("hero.eyebrow")}
        </motion.p>

        <h1 className="font-display font-black tracking-tight text-white leading-[0.98] text-5xl sm:text-7xl lg:text-8xl" data-testid="hero-headline">
          <MaskedLine delay={0.35}>{t("hero.title1")}</MaskedLine>
          <MaskedLine delay={0.5}>
            <span className="gold-gradient-text">{t("hero.title2")}</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
          className="mt-6 text-lg sm:text-xl text-slate-300 font-medium max-w-2xl"
          data-testid="hero-subheadline"
        >
          {t("hero.sub")}
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1, ease: EASE }}
          className="mt-4 text-sm sm:text-base text-slate-400 leading-relaxed max-w-xl"
        >
          {t("hero.desc")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.15, ease: EASE }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <button
            onClick={() => scrollToSection("enquiry")}
            data-testid="hero-consultation-btn"
            className="btn-gold rounded-full px-8 py-4 text-sm font-bold tracking-wider font-display"
          >
            {t("hero.cta1")}
          </button>
          <button
            onClick={() => scrollToSection("eligibility")}
            data-testid="hero-eligibility-btn"
            className="btn-outline-light rounded-full px-8 py-4 text-sm font-bold tracking-wider font-display"
          >
            {t("hero.cta2")}
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3"
          data-testid="hero-trust-strip"
        >
          {TRUST.map((item, i) => (
            <span key={i} className="flex items-center gap-2.5 text-sm text-slate-300">
              <item.icon className="h-4 w-4 text-gold" />
              {t(`hero.trust.${i}`)}
            </span>
          ))}
        </motion.div>
      </div>

      <motion.button
        onClick={() => scrollToSection("enquiry")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-slate-400 hover:text-gold transition-colors"
        aria-label="Scroll down"
        data-testid="hero-scroll-cue"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }} className="block">
          <ChevronDown className="h-6 w-6" />
        </motion.span>
      </motion.button>
    </section>
  );
};

export default Hero;
